import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogPublicAPI, blogImageURL } from '../lib/blogApi';
import { Loader2, Search, Calendar, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  useEffect(() => {
    blogPublicAPI.categories().then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, per_page: 12 };
    if (activeCat) params.category = activeCat;
    if (search.trim()) params.q = search.trim();
    blogPublicAPI.list(params)
      .then(r => {
        setPosts(r.data.posts || []);
        setPages(r.data.pages || 1);
      })
      .finally(() => setLoading(false));
  }, [page, activeCat, search]);

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white" data-testid="blog-list-page">
      {/* Hero */}
      <section className="relative pt-28 pb-16 px-6 border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <Link to="/" className="text-sm text-slate-400 hover:text-amber-400 transition-colors">← Back to home</Link>
          <div className="mt-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-xs uppercase tracking-widest text-amber-400/80 font-medium">DotCall Blog</span>
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] leading-tight">
            Insights on AI Voice,<br />
            <span className="text-amber-400">Sales & Support Automation</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-2xl">
            Practical guides, case studies, and product updates — written by humans and curated AI to help you scale calls intelligently.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-xl relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search articles..."
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              data-testid="blog-search-input"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="border-b border-white/5">
          <div className="max-w-6xl mx-auto px-6 py-5 flex gap-2 overflow-x-auto">
            <button
              onClick={() => { setPage(1); setActiveCat(null); }}
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${!activeCat ? 'bg-amber-500 text-black font-medium' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
              data-testid="blog-cat-all"
            >
              All
            </button>
            {categories.map(c => (
              <button
                key={c.name}
                onClick={() => { setPage(1); setActiveCat(c.name); }}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${activeCat === c.name ? 'bg-amber-500 text-black font-medium' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                data-testid={`blog-cat-${c.name.toLowerCase().replace(/\s+/g,'-')}`}
              >
                {c.name} <span className="opacity-60">({c.count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Posts */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="blog-empty-state">
            <p className="text-lg">No posts found.</p>
            <p className="text-sm mt-2">Check back soon — fresh content drops daily.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="blog-posts-grid">
            {posts.map(p => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group flex flex-col bg-[#0B1221]/80 border border-white/5 rounded-2xl overflow-hidden hover:border-amber-500/40 transition-all"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="aspect-video bg-gradient-to-br from-amber-500/10 to-purple-500/10 overflow-hidden">
                  {p.featured_image_url ? (
                    <img src={blogImageURL(p.featured_image_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-amber-500/30">
                      <Sparkles className="w-12 h-12" />
                    </div>
                  )}
                </div>
                <div className="flex-1 p-6 flex flex-col">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    {p.category && <Badge variant="outline" className="border-amber-500/30 text-amber-400">{p.category}</Badge>}
                    {p.ai_generated && <span className="text-purple-400">✨ AI</span>}
                  </div>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-white group-hover:text-amber-400 transition-colors line-clamp-2 mb-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-slate-400 line-clamp-3 mb-4 flex-1">{p.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-500 pt-4 border-t border-white/5">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(p.published_at || p.created_at)}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{p.reading_time || 5} min read</span>
                    <ArrowRight className="w-3 h-3 ml-auto group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-12">
            {Array.from({ length: pages }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`w-10 h-10 rounded-lg text-sm transition-all ${page === n ? 'bg-amber-500 text-black font-semibold' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                data-testid={`blog-page-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
